'use client';

import { type KeyboardEvent, useEffect } from 'react';

import { PurchaseButton } from '../product-details';
import { MiniPdpDisclaimers } from './mini-pdp-disclaimers';
import { MiniPdpFulfillment } from './mini-pdp-fulfillment';
import { MiniPdpQuantity } from './mini-pdp-quantity';
import { MiniPdpVariants } from './mini-pdp-variants';
import { type UseMiniPdpAddToCartProps, useMiniPdpAddToCart } from './model/use-mini-pdp-add-to-cart';
import { useMiniPdpBopis } from './model/use-mini-pdp-bopis';
import type { MiniPdpFulfillmentProps } from './model/use-mini-pdp-fulfillment';
import type { MiniPdpVariantsProps } from './model/use-mini-pdp-variants';

type MiniPdpPurchaseFormProps = UseMiniPdpAddToCartProps &
    MiniPdpVariantsProps &
    Pick<MiniPdpFulfillmentProps, 'onOpenSddAddress' | 'onOpenStoreDrawer'> & {
        onErrorChange?: (isError: boolean) => void;
    };

const preventImplicitSubmit = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement)) {
        event.preventDefault();
    }
};

export function MiniPdpPurchaseForm({
    base,
    product,
    tailored,
    isLoading,
    isError = false,
    onErrorChange,
    onOpenSddAddress,
    onOpenStoreDrawer,
}: MiniPdpPurchaseFormProps) {
    const {
        disabled,
        isError: isAddToCartError,
        isSubmitting,
        isSuccess,
        submitHandler,
    } = useMiniPdpAddToCart({
        base,
        product,
        tailored,
        isLoading,
        isError,
    });
    const { shouldRenderNearbyStoresCTA } = useMiniPdpBopis({ product, tailored, isLoading, isError });

    useEffect(() => {
        onErrorChange?.(isAddToCartError);
        return () => onErrorChange?.(false);
    }, [isAddToCartError, onErrorChange]);

    return (
        <form
            id="mini-pdp-form"
            data-testid="mini-pdp-form"
            className="mini-pdp-drawer__form"
            onKeyDown={preventImplicitSubmit}
            onSubmit={submitHandler}
        >
            {product && (
                <>
                    <MiniPdpVariants base={base} tailored={tailored} />
                    <div className="mini-pdp-drawer__purchase">
                        <MiniPdpFulfillment
                            product={product}
                            tailored={tailored}
                            isLoading={isLoading}
                            isError={isError}
                            onOpenSddAddress={onOpenSddAddress}
                            onOpenStoreDrawer={onOpenStoreDrawer}
                        />
                        <MiniPdpDisclaimers product={product} tailored={tailored} />
                        {shouldRenderNearbyStoresCTA ? (
                            <div className="mini-pdp-drawer__purchase-row">
                                <PurchaseButton
                                    type="button"
                                    text="Show in-stock stores"
                                    testId="mini-pdp-show-in-stock-stores"
                                    onClick={onOpenStoreDrawer}
                                />
                            </div>
                        ) : (
                            <div className="mini-pdp-drawer__purchase-row">
                                <MiniPdpQuantity
                                    product={product}
                                    tailored={tailored}
                                    isLoading={isLoading}
                                    isError={isError}
                                />
                                <PurchaseButton
                                    text={isSuccess ? 'Added to cart' : 'Add to cart'}
                                    disabled={disabled}
                                    loading={isSubmitting}
                                    isSuccess={isSuccess}
                                    formId="mini-pdp-form"
                                    testId="mini-pdp-add-to-cart"
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </form>
    );
}
